import { createMuiTheme } from "@material-ui/core/styles";

import {
  ACCENT,
  ACCENT_ACTIVE,
  ACCENT_HOVER,
  ACCENT_TEXT,
  darkSurfaces,
  DRAWER_WIDTH,
  FONT_BODY,
  FONT_DISPLAY,
  lightSurfaces,
  RADIUS_CTA,
  RADIUS_INPUT,
  RADIUS_PANEL,
  STATUS_ERR,
  STATUS_OK,
  STATUS_WARN,
} from "./tokens";

const buildTheme = (type) => {
  const isDark = type === "dark";
  const surfaces = isDark ? darkSurfaces : lightSurfaces;

  return {
    palette: {
      type: type,
      primary: {
        main: ACCENT,
        dark: ACCENT_ACTIVE,
        light: ACCENT_HOVER,
        contrastText: "#ffffff",
      },
      // secondary — вспомогательный синий, а НЕ «цвет подтверждения» и не цвет
      // ошибки: в апстриме им были покрашены и Cancel, и Create, и текст
      // ошибки. Роли разведены явно по color="primary"/"error" на местах.
      secondary: {
        main: ACCENT_TEXT,
        contrastText: isDark ? "#000000" : "#ffffff",
      },
      error: { main: STATUS_ERR },
      warning: { main: STATUS_WARN },
      success: { main: STATUS_OK },
      divider: surfaces.hairline,
      background: { default: surfaces.bg, paper: surfaces.surface1 },
      text: {
        primary: surfaces.fg,
        secondary: surfaces.fgDim,
        disabled: surfaces.fgMuted,
        hint: surfaces.fgMuted,
      },
      action: {
        hover: surfaces.surface3,
        selected: "rgba(79,79,244,0.12)",
      },
    },

    shape: { borderRadius: RADIUS_PANEL },

    typography: {
      fontFamily: FONT_BODY,
      // Заголовки KODE: 800, line-height 1.1, отрицательный трекинг.
      h1: {
        fontFamily: FONT_DISPLAY,
        fontWeight: 800,
        fontSize: "clamp(32px, 3vw, 60px)",
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontFamily: FONT_DISPLAY,
        fontWeight: 800,
        fontSize: 40,
        lineHeight: 1.1,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontFamily: FONT_DISPLAY,
        fontWeight: 800,
        fontSize: 32,
        lineHeight: 1.1,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontFamily: FONT_DISPLAY,
        fontWeight: 800,
        fontSize: 24,
        lineHeight: 1.15,
        letterSpacing: "-0.01em",
      },
      h5: { fontWeight: 600, fontSize: 18, lineHeight: 1.3 },
      h6: { fontWeight: 600, fontSize: 15, lineHeight: 1.4 },
      body1: { fontSize: 15, lineHeight: 1.5 },
      body2: { fontSize: 13, lineHeight: 1.5 },
      // UPPERCASE только у кнопок и надзаголовков — и всегда с большим трекингом.
      button: {
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
      overline: {
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        lineHeight: 1.4,
      },
      caption: { fontSize: 11, letterSpacing: "0.02em" },
    },

    props: {
      MuiPaper: { elevation: 0 },
      MuiAppBar: { elevation: 0 },
      MuiButton: { disableElevation: true },
      MuiTooltip: { arrow: true },
      MuiDialog: { maxWidth: "sm", fullWidth: true },
      MuiCircularProgress: { color: "primary" },
    },

    overrides: {
      MuiCssBaseline: {
        "@global": {
          html: { backgroundColor: surfaces.bg },
          body: {
            backgroundColor: surfaces.bg,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          },
          "::selection": { backgroundColor: "rgba(79,79,244,0.35)" },
        },
      },

      // Без этого <AppBar> заливается сплошным индиго на всю ширину.
      MuiAppBar: {
        colorPrimary: {
          backgroundColor: isDark ? "rgba(10,10,10,0.86)" : "rgba(255,255,255,0.86)",
          color: surfaces.fg,
          borderBottom: `1px solid ${surfaces.hairline}`,
          boxShadow: "none",
        },
      },

      MuiDrawer: {
        paper: {
          width: DRAWER_WIDTH,
          backgroundColor: surfaces.surface2,
          borderRight: `1px solid ${surfaces.hairline}`,
          backgroundImage: "none",
        },
      },

      // Глубина у KODE — поверхность плюс волосяная линия, а не тень.
      MuiPaper: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: RADIUS_PANEL },
        elevation0: { boxShadow: "none" },
        elevation1: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
        elevation2: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
        elevation3: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
        elevation4: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
        elevation8: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
        elevation24: { boxShadow: "none", border: `1px solid ${surfaces.hairline}` },
      },

      MuiButton: {
        root: { borderRadius: RADIUS_CTA, minHeight: 40, padding: "0 20px" },
        contained: {
          boxShadow: "none",
          backgroundColor: surfaces.surface3,
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          backgroundColor: ACCENT,
          "&:hover": { backgroundColor: ACCENT_HOVER },
          "&:active": { backgroundColor: ACCENT_ACTIVE },
        },
        outlined: {
          borderColor: surfaces.hairline,
          color: surfaces.fg,
          "&:hover": {
            borderColor: ACCENT,
            backgroundColor: "rgba(79,79,244,0.08)",
          },
        },
        text: { color: surfaces.fgDim },
      },

      // 3.76:1 у акцента проходит порог 3:1 без запаса — поэтому кольцо фокуса
      // всегда идёт в паре с мягким ореолом, чтобы читаться и на surface-3.
      MuiButtonBase: {
        root: {
          "&.Mui-focusVisible": {
            outline: `2px solid ${ACCENT}`,
            outlineOffset: 2,
            boxShadow: "0 0 0 4px rgba(79,79,244,0.25)",
          },
        },
      },

      MuiIconButton: {
        root: {
          borderRadius: RADIUS_INPUT,
          color: surfaces.fgDim,
          "&:hover": { backgroundColor: surfaces.surface3, color: surfaces.fg },
        },
        colorInherit: { color: "inherit" },
      },

      MuiListItem: {
        root: { minHeight: 36 },
        button: {
          "&:hover": {
            backgroundColor: surfaces.surface3,
            boxShadow: `inset 2px 0 0 ${ACCENT}`,
          },
        },
      },

      MuiDialog: {
        paper: {
          backgroundColor: surfaces.surface1,
          border: `1px solid ${surfaces.hairline}`,
          borderRadius: RADIUS_PANEL,
          boxShadow: "none",
        },
      },
      MuiDialogTitle: {
        root: {
          padding: "24px 24px 8px",
          "& .MuiTypography-root": {
            fontFamily: FONT_DISPLAY,
            fontWeight: 800,
            fontSize: 20,
            letterSpacing: "-0.01em",
          },
        },
      },
      MuiDialogActions: {
        root: {
          borderTop: `1px solid ${surfaces.hairline}`,
          padding: "16px 24px",
          marginTop: 16,
        },
      },

      MuiOutlinedInput: {
        root: { borderRadius: RADIUS_INPUT, backgroundColor: surfaces.surface2 },
        notchedOutline: { borderColor: surfaces.hairline },
      },
      MuiInput: {
        underline: {
          "&:before": { borderBottomColor: surfaces.hairline },
          "&:hover:not(.Mui-disabled):before": {
            borderBottomColor: surfaces.hairlineStrong,
          },
        },
      },
      MuiSelect: { icon: { color: surfaces.fgDim } },

      MuiSwitch: {
        track: { backgroundColor: surfaces.hairlineStrong, opacity: 1 },
        thumb: { boxShadow: "none" },
        colorSecondary: {
          "&$checked": { color: ACCENT },
          "&$checked + $track": { backgroundColor: "rgba(79,79,244,0.35)", opacity: 1 },
        },
      },

      MuiBackdrop: { root: { backgroundColor: surfaces.scrim } },

      MuiTooltip: {
        tooltip: {
          backgroundColor: surfaces.surface3,
          border: `1px solid ${surfaces.hairline}`,
          borderRadius: RADIUS_INPUT,
          color: surfaces.fg,
          fontSize: 11,
          padding: "6px 10px",
        },
        arrow: { color: surfaces.surface3 },
      },

      MuiMenu: { paper: { border: `1px solid ${surfaces.hairline}` } },
      MuiMenuItem: { root: { minHeight: 36, fontSize: 13 } },
      MuiDivider: { root: { backgroundColor: surfaces.hairline } },
    },
  };
};

// Строятся один раз на уровне модуля: в апстриме createMuiTheme вызывался в
// render(), и JSS перехешировал и переинжектил листы на каждый рендер.
export const kodeDarkTheme = createMuiTheme(buildTheme("dark"));
export const kodeLightTheme = createMuiTheme(buildTheme("light"));
